"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useCartStore } from "@/lib/cart/cart-store";
import type { ValidatedCartItem } from "@/lib/cart/types";
import { getEffectivePrice } from "@/lib/cart/calculate-order-totals";
import { SELECTION_UNIT_KG, SELECTION_UNIT_PIECE, KG_QUANTITY_STEP } from "@/constants/misc";
import { displayCurrency } from "@/lib/utils";
import AddToCartControls from "@/components/common/add-to-cart-controls";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type CartItemRowProps = {
  item: ValidatedCartItem;
};

export default function CartItemRow({ item }: CartItemRowProps) {
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const addItem = useCartStore((s) => s.addItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);

  const handleAdd = () => {
    addItem(item.productId, item.unit, item.priceMeasurementUnit, item.canBePurchasedByUnit, item.canBePurchasedByKg, { displayPrice: item.displayPrice, pricePerPiece: item.pricePerPiece, pricePerKg: item.pricePerKg, name: item.name, imageUrl: item.imageUrl });
  };

  const step = item.unit === SELECTION_UNIT_KG ? KG_QUANTITY_STEP : 1;

  const handleDecrement = () => {
    const newQty = Math.round((item.quantity - step) * 10) / 10;
    if (newQty <= 0) {
      setConfirmRemoveOpen(true);
    } else {
      updateQuantity(item.productId, newQty);
    }
  };

  const handleConfirmRemove = () => {
    removeItem(item.productId);
    setConfirmRemoveOpen(false);
  };

  const handleIncrement = () => {
    updateQuantity(item.productId, Math.round((item.quantity + step) * 10) / 10);
  };

  const handleQuantityChange = (qty: number) => {
    if (qty <= 0) {
      removeItem(item.productId);
    } else {
      updateQuantity(item.productId, Math.round(qty * 10) / 10);
    }
  };

  const effectivePrice = getEffectivePrice(item);
  const subtotal = effectivePrice * item.quantity;
  const pmuSuffix = item.priceMeasurementUnit ? `/${item.priceMeasurementUnit}` : "";
  const showUnit = true; //(item.canBePurchasedByUnit && item.canBePurchasedByKg) || item.unit === SELECTION_UNIT_KG;

  return (
    <div className="flex gap-3 py-3 px-4">
      {/* Image */}
      <Link href={`/producto/${item.productId}`} className="flex-shrink-0">
        <div className="relative w-14 h-14 rounded-lg overflow-hidden">
          <Image
            src={item.imageUrl}
            alt={item.name}
            fill
            className="object-cover"
            sizes="56px"
          />
        </div>
      </Link>

      {/* Details and controls */}
      <div className="flex-1 min-w-0">
        <Link href={`/producto/${item.productId}`}>
          <h3 className="text-sm font-medium text-gray-800 line-clamp-1 hover:text-gray-900 transition">
            {item.name}
          </h3>
        </Link>

        <div className="flex items-center justify-between mt-1">
          <div>
            <p className="text-sm font-medium text-gray-700">{displayCurrency(subtotal)}</p>
            <p className="text-xs text-muted-foreground">
              {displayCurrency(item.displayPrice ?? effectivePrice)}{pmuSuffix}
            </p>
          </div>

          <AddToCartControls
            quantity={item.quantity}
            onAdd={handleAdd}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
            onQuantityChange={handleQuantityChange}
            variant="compact"
            unit={showUnit ? item.unit : undefined}
            maxQuantity={item.maxQuantity}
          />
        </div>
      </div>

      {/* Confirm remove dialog */}
      <Dialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar producto</DialogTitle>
            <DialogDescription>
              ¿Estás seguro que deseas eliminar &quot;{item.name}&quot; de tu carrito?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="cursor-pointer mr-2" onClick={() => setConfirmRemoveOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" className="cursor-pointer" onClick={handleConfirmRemove}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
